import {
  ColumnDef,
  FilterFn,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { TableComponent } from 'components/table/TableComponent'
import {
  Expandable,
  useDefaultExpandableTableProps,
} from 'components/table/utility/expandableTable'
import React, { useMemo, useState } from 'react'
import { GeckoSiteGrouping } from 'types'

import { groupingsTableColumns } from './GroupingsTableColumns'

export interface GroupingsTableProps {
  existingGroupings: GeckoSiteGrouping[]
  handleEditGroupingInfo?: (row: Expandable<GeckoSiteGrouping>) => void
  selectionMode?: boolean
  selectedGroupingId?: string | undefined
  onSelectGrouping?: (groupingId: string) => void
}

export const GroupingsTable: React.FC<GroupingsTableProps> = ({
  existingGroupings,
  handleEditGroupingInfo,
  selectionMode = false,
  selectedGroupingId,
  onSelectGrouping,
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const isLoadingGroupingsData = false

  const {
    data: expandableGroupingsData,
    getRowCanExpand,
    getSubRows,
  } = useDefaultExpandableTableProps(existingGroupings)

  const columns: ColumnDef<Expandable<GeckoSiteGrouping>>[] = useMemo(() => {
    return groupingsTableColumns(
      handleEditGroupingInfo || (() => {}),
      selectionMode,
      selectedGroupingId,
      onSelectGrouping
    )
  }, [
    selectionMode,
    selectedGroupingId,
    onSelectGrouping,
    handleEditGroupingInfo,
  ])

  const globalFilterFn: FilterFn<Expandable<GeckoSiteGrouping>> = (
    row,
    _,
    filterValue
  ) => {
    if (typeof filterValue !== 'string') return true

    const searchValue = filterValue?.toLowerCase() || ''
    if (!searchValue) return true

    return Object.values(row.original).some((value) => {
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchValue)
      }
      return false
    })
  }

  return (
    <TableComponent
      isLoadingData={isLoadingGroupingsData}
      data={expandableGroupingsData}
      columns={columns}
      footerName='Grouping(s)'
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
      enableExpanding
      getRowCanExpand={getRowCanExpand}
      getSubRows={getSubRows}
      filterFromLeafRows
      showFilterBar
      showColumnConfig
      globalFilterFn={globalFilterFn}
      disableUrlFilters={selectionMode}
    />
  )
}
